// "Scan the menu board" → structured daily-menu items.
//   Backend configured → Cloud Function `extractDailyMenu` (AI vision, accurate).
//   Demo (no backend)  → on-device OCR via Tesseract.js (basic; English + prices).
import { httpsCallable } from "firebase/functions";
import { functions } from "./firebase";
import { isBackendConfigured } from "./catalog";
import { resizeImage } from "./image";

export interface ScannedItem {
  name: string;
  nameAr: string;
  price: number;
  qty: number;
}

export interface ScanResult {
  items: ScannedItem[];
  mode: "ai" | "device";
}

const AI_SCAN_ENABLED = import.meta.env.VITE_AI_SCAN_ENABLED === "true";

export async function scanMenuBoard(file: File): Promise<ScanResult> {
  const { dataUrl } = await resizeImage(file, 1400, 0.85);

  // AI vision only when a backend + Anthropic key are wired; else on-device OCR.
  if (isBackendConfigured && AI_SCAN_ENABLED) {
    const base64 = dataUrl.split(",")[1];
    const call = httpsCallable<
      { imageBase64: string; mediaType: string },
      { items: ScannedItem[] }
    >(functions, "extractDailyMenu");
    const res = await call({ imageBase64: base64, mediaType: "image/jpeg" });
    return { items: normalize(res.data.items ?? []), mode: "ai" };
  }

  return { items: await deviceOcr(dataUrl), mode: "device" };
}

async function deviceOcr(dataUrl: string): Promise<ScannedItem[]> {
  const { recognize } = await import("tesseract.js");
  const { data } = await recognize(dataUrl, "eng");
  return normalize(parseLines(data.text));
}

/** Heuristic parse: lines that end in a price become items. */
function parseLines(text: string): ScannedItem[] {
  const items: ScannedItem[] = [];
  for (const raw of text.split("\n")) {
    const line = raw.trim();
    if (line.length < 3) continue;
    const m = line.match(/(\d{1,3}(?:[.,]\d{1,3})?)\s*(?:kd|kwd|د\.?\s*ك)?\s*$/i);
    if (!m || m.index == null) continue;
    const price = parseFloat(m[1].replace(",", "."));
    if (!(price > 0) || price > 100) continue;
    const name = line
      .slice(0, m.index)
      .replace(/[.\-–·:]+\s*$/, "")
      .trim();
    if (name.length < 2) continue;
    items.push({ name, nameAr: "", price, qty: 10 });
  }
  return items;
}

function normalize(items: ScannedItem[]): ScannedItem[] {
  return items
    .filter((it) => it && it.name && it.name.trim())
    .map((it) => ({
      name: String(it.name).trim(),
      nameAr: String(it.nameAr ?? "").trim(),
      price: Math.round((Number(it.price) || 0) * 1000) / 1000,
      qty: Math.max(0, Math.floor(Number(it.qty) || 10)),
    }));
}
